import { WritableSignal } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

import { Payload } from '@shared/client/models/common.model';

export abstract class AbstractSecurityService<T> {
    public static accessTokenLocalStorageKey = 'access_token';
    public abstract meSignal: WritableSignal<T | null>;

    public static getAccessToken(): string | null {
        return localStorage.getItem(this.accessTokenLocalStorageKey);
    }

    public decodeToken(token: string): Payload | null {
        try {
            return jwtDecode<Payload>(token);
        } catch {
            return null;
        }
    }

    public async initSession(): Promise<void> {
        const token = localStorage.getItem(AbstractSecurityService.accessTokenLocalStorageKey);
        await this.setSession(token);
    }

    public isAuthenticated(): boolean {
        const token = AbstractSecurityService.getAccessToken();
        if (token === null) {
            return false;
        }

        return this.isTokenValid(token);
    }

    public isPayloadExpired(payload: Payload): boolean {
        // As JWT exp date is expressed in seconds and Date.now() in milliseconds, we need to do a conversion to compare those two.
        return Date.now() > payload.exp * 1000;
    }

    public isTokenValid(token: string): boolean {
        const payload = this.decodeToken(token);
        if (!payload) {
            return false;
        }

        return !this.isPayloadExpired(payload);
    }

    public async setSession(token: string | null): Promise<void> {
        if (!token) {
            localStorage.removeItem(AbstractSecurityService.accessTokenLocalStorageKey);
            this.meSignal.set(null);

            return;
        }

        const payload = this.decodeToken(token);

        if (payload) {
            localStorage.setItem(AbstractSecurityService.accessTokenLocalStorageKey, token);
            const me = await this.getMe();

            this.meSignal.set(me);

            return;
        }

        await this.setSession(null);
    }

    public async updateMe(): Promise<void> {
        const updatedMe = await this.getMe();

        if (!updatedMe) {
            return;
        }

        this.meSignal.set(updatedMe);
    }

    public abstract changePassword(password: string, token: string): Promise<void>;

    public abstract forgotPassword(login: string): Promise<void>;

    public abstract getMe(): Promise<T>;

    public abstract login(login: string, password: string): Promise<void>;

    public abstract logout(): Promise<boolean>;
}
